/**
 * AIAccuracyCard — "This week's AI accuracy" mini widget for the recruiter
 * dashboard. Reads /api/ai-feedback/summary?days=7 and shows the % of AI
 * suggestions that got accepted vs. dismissed/flagged as false positives.
 *
 * Clicking anywhere on the card opens the raw feedback list filtered by
 * false-positive events so the recruiter (or admin) can inspect what the AI
 * got wrong this week — the whole point of the feedback loop.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Sparkles, ArrowUpRight, ThumbsUp, ThumbsDown } from "lucide-react";

export default function AIAccuracyCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/ai-feedback/summary", { params: { days: 7 } });
                setData(data);
            } catch { /* silent */ }
            finally { setLoading(false); }
        })();
    }, []);

    if (loading) {
        return (
            <div className="border border-border bg-white p-4 sm:p-5" data-testid="ai-accuracy-card">
                <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">Loading AI accuracy…</div>
            </div>
        );
    }
    if (!data || (data.total || 0) === 0) {
        return (
            <div className="border border-border bg-white p-4 sm:p-5" data-testid="ai-accuracy-card">
                <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-ikb" />
                    <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">This week&apos;s AI accuracy</div>
                </div>
                <div className="text-sm text-muted-foreground">
                    No AI feedback captured yet. Rate red-flag cards and AI summaries with 👍 / 👎 to see accuracy trends here.
                </div>
            </div>
        );
    }

    const acc = data.accuracy_pct;
    const accColor = acc == null ? "text-muted-foreground"
        : acc >= 80 ? "text-emerald-700"
        : acc >= 60 ? "text-amber-600"
        : "text-rose-600";
    const up = (data.actions?.accept || 0) + (data.actions?.complete || 0);
    const down = (data.actions?.false_positive || 0) + (data.actions?.dismiss || 0);
    const topReasons = (data.top_reasons || []).slice(0, 3);

    return (
        <Link
            to="/ai-feedback"
            className="border border-border bg-white p-4 sm:p-5 block hover:border-ikb transition-colors group"
            data-testid="ai-accuracy-card"
        >
            <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-ikb" />
                <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground flex-1">This week&apos;s AI accuracy</div>
                <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-ikb transition-colors" />
            </div>
            <div className="flex items-baseline gap-2">
                <span className={`font-display font-black text-3xl sm:text-4xl tracking-tight ${accColor}`} data-testid="ai-accuracy-pct">
                    {acc == null ? "—" : `${acc}%`}
                </span>
                <span className="text-xs font-mono text-muted-foreground">of AI calls agreed</span>
            </div>
            <div className="mt-2 flex items-center gap-3 text-[11px] font-mono">
                <span className="inline-flex items-center gap-1 text-emerald-700"><ThumbsUp className="w-3 h-3" /> {up} accepted</span>
                <span className="inline-flex items-center gap-1 text-rose-700"><ThumbsDown className="w-3 h-3" /> {down} flagged wrong</span>
                <span className="text-muted-foreground">· {data.total} events</span>
            </div>
            {topReasons.length > 0 && (
                <div className="mt-3 pt-3 border-t border-border">
                    <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Top mismatches</div>
                    <ul className="space-y-0.5">
                        {topReasons.map((r) => (
                            <li key={r.reason} className="text-[11px] text-foreground/80 truncate">
                                <span className="text-muted-foreground font-mono mr-1">{r.count}×</span>{r.reason}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </Link>
    );
}
